//  _        _      _         _
// | |  _  _| |_  _| |__ _  _| |__ _  _
// | |_| || | | || | '_ \ || | '_ \ || |
// |____\_,_|_|\_,_|_.__/\_,_|_.__/\_,_|
//
// Copyright © Lulububu Software GmbH - All Rights Reserved
// https://lulububu.de
//
// Unauthorized copying of this file, via any medium is strictly prohibited!
// Proprietary and confidential.

import { call }       from 'redux-saga/effects';
import { put }        from 'redux-saga/effects';
import { takeLatest } from 'redux-saga/effects';
import I18n           from 'i18next';

import { SignupActions }     from '@/store/actions/signup';
import { SignupTypes }       from '@/store/actions/signup';
import { AlertBoxActions }   from '@/store/actions/alertBox';
import * as Api              from '@/api';
import PasswordStrength      from '@/constants/PasswordStrength';
import Hydra                 from '@/helper/Hydra';
import { NavigationActions } from '@/store/actions/navigation';
import Routes                from '@/constants/Routes';

function* signup(action) {
    yield put(AlertBoxActions.clearAlerts());

    // @formatter:off
    const {
        name,
        identifier,
        password,
        repeatedPassword,
        passwordScore,
        company,
        membershipData,
    }                            = action;
    // @formatter:on
    const isPasswordSecureEnough = yield PasswordStrength.isPasswordSecureEnough(
        passwordScore,
        password,
        repeatedPassword,
    );

    if (!isPasswordSecureEnough) {
        return;
    }

    let signupObject = {
        name,
        email:         identifier,
        plainPassword: password,
        company,
    };

    if (membershipData) {
        signupObject = {
            ...signupObject,
            membershipRequest: {
                companyName:                     membershipData.companyName,
                address:                         membershipData.address,
                city:                            membershipData.city,
                state:                           membershipData.state,
                postalCode:                      membershipData.postalCode,
                taxId:                           membershipData.taxId,
                promotionCode:                   membershipData.promotionCode,
                preferredOnsiteVerificationDate: membershipData.preferredOnsiteVerificationDate,
                country:                         membershipData.country?.label,
                membership:                      Hydra.getIdFromIri(membershipData.membership?.iri),
            },
        };
    }

    const signupResponse = yield call(
        Api.signup,
        signupObject,
    );

    if (signupResponse.ok) {
        yield put(SignupActions.signupSucceeded());
    } else {
        yield put(SignupActions.signupFailed({
            errorCode: signupResponse?.data?.error,
        }));
    }
}

function* signupSucceeded() {
    yield put(AlertBoxActions.showSuccessAlert({
        text:        I18n.t('signupSucceeded'),
        lifeCounter: 2,
    }));

    yield put(NavigationActions.openUrl({
        url: Routes.login,
    }));

    yield put(SignupActions.resetSignup());
}

function* signupFailed(action) {
    const errorMessage = I18n.t(`errors.${action.errorCode}`);

    yield put(AlertBoxActions.showErrorAlert({
        text: I18n.t(
            'signupFailed',
            {
                errorMessage,
            },
        ),
    }));
}

export const callSignupSagas = () => {
    return [
        // @formatter:off
        takeLatest([SignupTypes.SIGNUP],           signup),
        takeLatest([SignupTypes.SIGNUP_FAILED],    signupFailed),
        takeLatest([SignupTypes.SIGNUP_SUCCEEDED], signupSucceeded),
        // @formatter:on
    ];
};
